import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { Mail } from "lucide-react";
import { trackNavigation } from "@/utils/analytics";

const footerSections = [
  {
    title: "Valuations",
    links: [
      { name: "Instant Valuation", href: "/valuation" },
      { name: "Professional Yacht Appraisal", href: "/boat-appraisal" },
      { name: "AI Yacht Valuation", href: "/ai-boat-valuation" },
    ],
  },
  {
    title: "Yacht Types",
    links: [
      { name: "Motor Yacht Values", href: "/boat-value/motor-yachts" },
      { name: "Sport Yacht Values", href: "/boat-value/sport-yachts" },
      { name: "Flybridge Yacht Values", href: "/boat-value/flybridge-yachts" },
      { name: "Express Cruiser Values", href: "/boat-value/express-cruisers" },
      { name: "Trawler Yacht Values", href: "/boat-value/trawler-yachts" },
      { name: "Catamaran Values", href: "/boat-value/catamarans" },
      { name: "Sailboat Values", href: "/boat-value/sailboats" },
    ],
  },
  {
    title: "Guides",
    links: [
      { name: "Yacht Buying Guide", href: "/guides/boat-buying" },
      { name: "Yacht Selling Guide", href: "/guides/boat-selling" },
      { name: "Yacht Depreciation", href: "/guides/boat-depreciation" },
    ],
  },
  {
    title: "Company",
    links: [
      { name: "About", href: "/about" },
      { name: "Contact", href: "/contact" },
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms of Use", href: "/terms" },
      { name: "Premium Terms", href: "/premium-terms" },
      { name: "Disclaimers", href: "/disclaimers" },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="theme-dark bg-background text-foreground border-t border-accent/20" style={{ contain: 'layout style' }}>
      <div className="container mx-auto px-4 py-16">
        <div className="h-px bg-accent/30 max-w-3xl mx-auto mb-12" aria-hidden="true" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex items-center mb-4">
              <Logo variant="inverse" size="lg" />
            </Link>
            <div className="h-px w-16 bg-accent/40 mb-5" aria-hidden="true" />
            <p className="text-muted-foreground text-sm mb-6 max-w-sm leading-relaxed">
              Private yacht valuation and market intelligence for luxury vessels.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-2 text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary/50" />
                <span>contact@yachtvalues.com</span>
              </div>
            </div>
          </div>

          {/* Footer Sections */}
          {footerSections.map((section, index) => (
            <div key={index}>
              <h3 className="font-medium text-foreground/80 mb-4 text-sm uppercase tracking-wider">{section.title}</h3>
              <ul className="space-y-1">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      to={link.href}
                      className="text-muted-foreground hover:text-primary transition-colors duration-300 text-sm inline-block py-2 min-h-[44px] flex items-center"
                      onClick={() => trackNavigation(link.name, link.href, 'footer')}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Brand note */}
        <div className="border-t border-border/10 mt-12 pt-6">
          <p className="text-xs text-muted-foreground text-center leading-relaxed max-w-2xl mx-auto">
            YachtValues™ provides yacht valuation and market intelligence for luxury vessels.
          </p>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-border/10 mt-6 pt-8 pb-safe">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 md:gap-0">
            <div className="text-sm text-muted-foreground">
              <p>&copy; 2017 – {new Date().getFullYear()} Coast Ventures LLC. All rights reserved.</p>
            </div>
            
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors duration-300 py-2 min-h-[44px] flex items-center">
                Privacy
              </Link>
              <span className="text-muted-foreground/70 hidden sm:inline" aria-hidden="true">·</span>
              <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors duration-300 py-2 min-h-[44px] flex items-center">
                Terms
              </Link>
              <span className="text-muted-foreground/70 hidden sm:inline" aria-hidden="true">·</span>
              <Link to="/do-not-sell" className="text-muted-foreground hover:text-primary transition-colors duration-300 py-2 min-h-[44px] flex items-center">
                Do Not Sell My Info
              </Link>
              <span className="text-muted-foreground/70 hidden sm:inline" aria-hidden="true">·</span>
              <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors duration-300 py-2 min-h-[44px] flex items-center">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
